/**
 * **控件编辑回调 + 预览文本** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 *   `onWidgetResize`       多行文本域被拖高（节点高度自适应 → 等于「把节点改高」）
 *   `onToggleHideUnused`   只折叠**这一个**节点的端口（Ctrl+H 是所有选中节点）
 *   `onToggleMute`         单节点静音开关
 *   `componentPreview`     组件预览：显示求值出来的实际文本，不是写死的占位
 *   `sourcePreview`        数据源预览
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 */
import { nextTick } from 'vue'
import { formatValue, valueKind } from '@/node/evaluate'
import type { EvalResult } from '@/node/evaluate'
import type { GraphNodeModel } from '@/types/node'
import type { FlowStoreApi } from '@/graph/flowStore'

export interface WidgetEditingCtx {
  flowStore: { value: FlowStoreApi | null }
  /** 求值结果（声明得比这段晚 —— 只在回调里读，闭包注入即可） */
  evalResult: () => EvalResult
  modelOf: (id: string) => GraphNodeModel | undefined
  /** 调参面板里的行高（`t.rowHeight`） */
  rowHeight: () => number
  commit: (label: string, coalesceMs?: number) => void
}

export function useWidgetEditing(ctx: WidgetEditingCtx) {
  const { flowStore, evalResult, modelOf, rowHeight, commit } = ctx

/* ── 多行文本域拖高 ─────────────────────────────────────────── */

/** 文本域被拖高 —— 因为节点高度自适应，这就是「把节点改高」 */
function onWidgetResize(
  nodeId: string,
  rowId: string,
  widgetId: string,
  height: number,
) {
  const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId)
  if (row && (row.kind === 'property' || row.kind === 'input')) {
    const widget = row.widget
    if (widget && widget.id === widgetId && widget.kind === 'text') {
      widget.height = height
    }
  }
}

/** 静音开关 */
/** 只切换这一个节点的端口折叠（Ctrl+H 是所有选中节点） */
function onToggleHideUnused(nodeId: string) {
  const model = modelOf(nodeId)
  if (!model) return
  commit('折叠端口')
  model.hideUnused = !model.hideUnused
  nextTick(() => flowStore.value?.updateNodeInternals([nodeId]))
}

function onToggleMute(nodeId: string, muted: boolean) {
  commit('静音节点')
  const model = modelOf(nodeId)
  if (model) model.muted = muted
}

/** 组件预览：显示求值出来的实际文本，而不是写死的占位 */
function componentPreview(nodeId: string): string {
  const value = evalResult().outputs.get(nodeId)?.get('out')
  if (!value || typeof value !== 'object') return '—'
  return String((value as { text?: unknown }).text ?? '—')
}

/** 数据源预览 */
function sourcePreview(nodeId: string): string {
  const outputs = evalResult().outputs.get(nodeId)
  return String(outputs?.get('f_trackingNo') ?? '—')
}

  return {
    onWidgetResize,
    onToggleHideUnused,
    onToggleMute,
    componentPreview,
    sourcePreview,
  }
}
